package com.iregained.site.components.cms

// Import all your section components and data classes
import androidx.compose.runtime.Composable
import com.iregained.site.components.sections.about.sections.*
import com.iregained.site.components.sections.clinicians.sections.*
import com.iregained.site.components.sections.clinics.sections.*
import com.iregained.site.components.sections.contact.sections.*
import com.iregained.site.components.sections.corporate.sections.*
import com.iregained.site.components.sections.distributors.sections.*
import com.iregained.site.components.sections.error.NotFoundSection
import com.iregained.site.components.sections.error.NotFoundSectionData
import com.iregained.site.components.sections.error.defaultNotFoundData
import com.iregained.site.components.sections.financing.sections.*
import com.iregained.site.components.sections.home.sections.*
import com.iregained.site.components.sections.news.sections.*
import com.iregained.site.components.sections.patients.sections.*
import com.iregained.site.components.sections.product.sections.*
import com.iregained.site.components.sections.resources.sections.*
import com.iregained.site.components.sections.testimonials.sections.PatientsRecoveryStoriesData
import com.iregained.site.components.sections.testimonials.sections.PatientsRecoveryStoriesSection
import com.iregained.site.components.sections.testimonials.sections.TestimonialsBenefitsData
import com.iregained.site.components.sections.testimonials.sections.TestimonialsBenefitsSection
import com.iregained.site.components.sections.testimonials.sections.TestimonialsCtaData
import com.iregained.site.components.sections.testimonials.sections.TestimonialsCtaSection
import com.iregained.site.components.sections.testimonials.sections.defaultPatientsRecoveryStoriesData
import com.iregained.site.components.sections.testimonials.sections.defaultTestimonialsBenefitsData
import com.iregained.site.components.sections.testimonials.sections.defaultTestimonialsCtaData
import com.iregained.site.components.widgets.ClinicalApplicationsData
import com.iregained.site.components.widgets.ClinicalApplicationsSection
import com.iregained.site.components.widgets.defaultClinicalApplicationsData

// 1. The Sealed Class: Represents the JSON array objects coming from your CMS
sealed class CmsSection {
    // Home Page Sections
    data class HomeHeroSection(val data: HomeHeroSectionData = defaultHeroSectionData) : CmsSection()
    data class HomeClinicalAccreditationSection(val data: HomeClinicalAccreditationSectionData = defaultHomeClinicalAccreditationSectionData) :
        CmsSection()

    data class HomeStatsToggleSection(val data: HomeStatsToggleSectionData = defaultHomeStatsToggleSectionData) :
        CmsSection()

    data class HomeAudienceCardsSection(val data: HomeAudienceCardsSectionData = defaultHomeAudienceCardsSectionData) :
        CmsSection()

    data class HomeFeaturesZigZagSection(val data: HomeFeaturesZigZagSectionData = defaultHomeFeaturesZigZagSectionData) :
        CmsSection()

    data class HomeClinicalBenefitsSection(val data: HomeClinicalBenefitsSectionData = defaultHomeClinicalBenefitsSectionData) :
        CmsSection()

    data class HomeVideoTestimonialsSection(val data: HomeVideoTestimonialsSectionData = defaultHomeVideoTestimonialsSectionData) :
        CmsSection()

    data class HomePartnerLogosMarqueeSection(val data: HomePartnerLogosMarqueeSectionData = defaultHomePartnerLogosMarqueeSectionData) :
        CmsSection()

    data class HomeCtaSection(val data: HomeCtaSectionData = defaultHomeCtaSectionData) : CmsSection()

    // Product Page Sections
    data class ProductHeroSection(val data: ProductHeroSectionData = defaultProductHeroSectionData) : CmsSection()
    data class ProductClinicalNeedSection(val data: ProductClinicalNeedSectionData = defaultProductClinicalNeedData) :
        CmsSection()

    data class ProductClinicalClearanceSection(val data: ProductClinicalClearanceSectionData = defaultProductClearanceData) :
        CmsSection()

    data class ProductHowItWorksSection(val data: ProductHowItWorksSectionData = defaultProductHowItWorksData) :
        CmsSection()

    data class ProductDemonstrationSection(val data: ProductDemoSectionData = defaultDemoData) : CmsSection()
    data class ProductClinicalEfficacySection(val data: ProductClinicalEfficacySectionData = defaultProductEfficacySectionData) :
        CmsSection()

    data class ProductNeuroscienceBenefitsSection(val data: ProductNeuroscienceSectionData = defaultProductNeuroscienceData) :
        CmsSection()

    data class ProductAudienceRoutingSection(val data: ProductAudienceRoutingSectionData = defaultProductAudienceRoutingData) :
        CmsSection()

    data class ProductProcurementSection(val data: ProductProcurementSectionData = defaultProductProcurementData) :
        CmsSection()

    data class ProductFaqSection(val data: ProductFaqSectionData = defaultProductFaqData) : CmsSection()
    data class ProductCtaSection(val data: ProductCtaSectionData = defaultProductCtaData) : CmsSection()

    // Patient Page Sections
    data class PatientsHeroSection(val data: PatientsHeroSectionData = defaultPatientsHeroSectionData) : CmsSection()
    data class PatientsScienceEducationSection(val data: PatientsScienceEducationSectionData = defaultPatientsScienceEducationSectionData) :
        CmsSection()

    data class PatientsMyHandFeaturesSection(val data: PatientsMyHandFeaturesSectionData = defaultPatientsMyHandFeaturesSectionData) :
        CmsSection()

    data class PatientsRecoveryProtocolsSection(val data: PatientsRecoveryProtocolsSectionData = defaultPatientsRecoveryProtocolsSectionData) :
        CmsSection()

    data class PatientsFeaturesAccordionSection(val data: PatientsFeaturesAccordionSectionData = defaultPatientsFeaturesAccordionSectionData) :
        CmsSection()

    data class PatientsPricingOptionsSection(val data: PatientsPricingOptionsData = defaultPatientsPricingData) :
        CmsSection()

    data class PatientsFaqSection(val data: PatientsFaqData = defaultPatientsFaqData) : CmsSection()
    data class PatientsCtaSection(val data: PatientsCtaSectionData = defaultPatientsCtaSectionData) : CmsSection()

    //  Clinicians Page Section
    data class CliniciansHeroSection(val data: CliniciansHeroSectionData = defaultCliniciansHeroSectionData) :
        CmsSection()

    data class CliniciansImpactBarSection(val data: CliniciansImpactBarData = defaultCliniciansImpactBarData) :
        CmsSection()

    data class CliniciansWorkflowSection(val data: CliniciansWorkflowSectionData = defaultCliniciansWorkflowData) :
        CmsSection()

    data class CliniciansImplementationSection(val data: CliniciansImplementationData = defaultCliniciansImplementationData) :
        CmsSection()

    data class CliniciansTargetedMessagingSection(val data: CliniciansTargetedMessagingData = defaultCliniciansTargetedMessagingData) :
        CmsSection()

    data class CliniciansEvidenceSection(val data: CliniciansEvidenceSectionData = defaultCliniciansEvidenceData) :
        CmsSection()

    data class CliniciansProcurementSection(val data: CliniciansProcurementSectionData = defaultCliniciansProcurementData) :
        CmsSection()

    data class CliniciansFaqSection(val data: CliniciansFaqData = defaultCliniciansFaqData) : CmsSection()

    data class CliniciansCtaSection(val data: CliniciansCtaData = defaultCliniciansCtaData) : CmsSection()

    //  About Page Section
    data class AboutHeroSection(val data: AboutHeroSectionData = defaultAboutHeroData) : CmsSection()
    data class AboutOriginSection(val data: AboutOriginSectionData = defaultAboutOriginData) : CmsSection()
    data class AboutFounderLetterSection(val data: AboutFounderLetterData = defaultAboutFounderLetterData) :
        CmsSection()

    data class AboutValuesSection(val data: AboutValuesSectionData = defaultAboutValuesData) : CmsSection()
    data class AboutLeadershipSection(val data: AboutLeadershipSectionData = defaultAboutLeadershipData) : CmsSection()
    data class AboutTrustSection(val data: AboutTrustSectionData = defaultAboutTrustData) : CmsSection()
    data class AboutCareersSection(val data: AboutCareersSectionData = defaultAboutCareersData) : CmsSection()
    data class AboutCtaSection(val data: AboutCtaData = defaultAboutCtaData) : CmsSection()

    //  Contact Page Section
    data class ContactRoutingSection(val data: ContactRoutingSectionData = defaultContactRoutingData) : CmsSection()
    data class ContactB2bDemoSection(val data: ContactB2bDemoData = defaultContactB2bData) : CmsSection()
    data class ContactPatientFormSection(val data: ContactPatientFormData = defaultContactPatientData) : CmsSection()
    data class ContactSupportSection(val data: ContactSupportSectionData = defaultContactSupportData) : CmsSection()

    // News Page Section
    data class NewsFeaturedSection(val data: NewsFeaturedSectionData = defaultNewsFeaturedData) : CmsSection()
    data class NewsGridSection(val data: NewsGridSectionData = defaultNewsGridData) : CmsSection()
    data class NewsPressKitSection(val data: NewsPressKitData = defaultNewsPressKitData) : CmsSection()

    // Resources Page Section
    data class ResourcesHeroSection(val data: ResourcesHeroSectionData = defaultResourcesHeroData) : CmsSection()
    data class ResourcesFeaturedSection(val data: ResourcesBrochureDownloadData = defaultResourcesBrochureDownloadData) :
        CmsSection()

    data class ResourcesGridSection(val data: ResourcesGridData = defaultResourcesGridData) : CmsSection()
    data class ResourcesCtaSection(val data: ResourcesCtaData = defaultResourcesCtaData) : CmsSection()

    // Testimonials Page Section
    data class TestimonialsBenefitsSection(val data: TestimonialsBenefitsData = defaultTestimonialsBenefitsData) :
        CmsSection()

    data class PatientsRecoveryStoriesSection(val data: PatientsRecoveryStoriesData = defaultPatientsRecoveryStoriesData) :
        CmsSection()

    data class TestimonialsCtaSection(val data: TestimonialsCtaData = defaultTestimonialsCtaData) : CmsSection()

    data class FinancingPartnersSection(val data: FinancingSectionData = defaultFinancingData) : CmsSection()
    data class FinancingCtaSection(val data: FinancingCtaData = defaultFinancingCtaData) : CmsSection()

    data class DistributorsHeroSection(val data: DistributorLocatorData = defaultDistributorData) : CmsSection()
    data class DistributorsListSection(val data: DistributorLocatorData = defaultDistributorData) : CmsSection()
    data class DistributorsCtaSection(val data: DistributorLocatorData = defaultDistributorData) : CmsSection()

    data class ClinicsHeroSection(val data: ClinicsLocatorData = defaultClinicsData) : CmsSection()
    data class ClinicsListSection(val data: ClinicsLocatorData = defaultClinicsData) : CmsSection()
    data class ClinicsCtaSection(val data: ClinicsLocatorData = defaultClinicsData) : CmsSection()

    // Common
    data class ClinicalApplicationsSection(val data: ClinicalApplicationsData = defaultClinicalApplicationsData) :
        CmsSection()

    data class CorporatePolicySection(val data: CorporatePolicySectionData) : CmsSection()
    data class CookieMatrixSection(val data: CookieMatrixSectionData = defaultCookieMatrixData) : CmsSection()
    data class TermsOfServiceSection(val data: TermsOfServiceSectionData = defaultTermsOfServiceData) : CmsSection()
    data class NotFoundSection(val data: NotFoundSectionData = defaultNotFoundData) : CmsSection()
}

// 2. The Renderer: Acts as a switchboard to render the correct UI for the CMS data block
@Composable
fun DynamicSectionRenderer(section: CmsSection) {
    when (section) {
        // Home
        is CmsSection.HomeHeroSection -> HomeHeroSection(data = section.data)
        is CmsSection.HomeClinicalAccreditationSection -> HomeClinicalAccreditationSection(data = section.data)
        is CmsSection.HomeStatsToggleSection -> HomeStatsToggleSection(data = section.data)
        is CmsSection.HomeAudienceCardsSection -> HomeAudienceCardsSection(data = section.data)
        is CmsSection.HomeFeaturesZigZagSection -> HomeFeaturesZigZagSection(data = section.data)
        is CmsSection.HomeClinicalBenefitsSection -> HomeClinicalBenefitsSection(data = section.data)
        is CmsSection.HomeVideoTestimonialsSection -> HomeVideoTestimonialsSection(data = section.data)
        is CmsSection.HomePartnerLogosMarqueeSection -> HomePartnerLogosMarqueeSection(data = section.data)
        is CmsSection.HomeCtaSection -> HomeCtaSection(data = section.data)

        // Product
        is CmsSection.ProductHeroSection -> ProductHeroSection(data = section.data)
        is CmsSection.ProductClinicalNeedSection -> ProductClinicalNeedSection(data = section.data)
        is CmsSection.ProductClinicalClearanceSection -> ProductClinicalClearanceSection(data = section.data)
        is CmsSection.ProductHowItWorksSection -> ProductHowItWorksSection(data = section.data)
        is CmsSection.ProductDemonstrationSection -> ProductDemonstrationSection(data = section.data)
        is CmsSection.ProductClinicalEfficacySection -> ProductClinicalEfficacySection(data = section.data)
        is CmsSection.ProductNeuroscienceBenefitsSection -> ProductNeuroscienceBenefitsSection(data = section.data)
        is CmsSection.ProductAudienceRoutingSection -> ProductAudienceRoutingSection(data = section.data)
        is CmsSection.ProductProcurementSection -> ProductProcurementSection(data = section.data)
        is CmsSection.ProductFaqSection -> ProductFaqSection(data = section.data)
        is CmsSection.ProductCtaSection -> ProductCtaSection(data = section.data)

        // Patients
        is CmsSection.PatientsHeroSection -> PatientsHeroSection(data = section.data)
        is CmsSection.PatientsScienceEducationSection -> PatientsScienceEducationSection(data = section.data)
        is CmsSection.PatientsMyHandFeaturesSection -> PatientsMyHandFeaturesSection(data = section.data)
        is CmsSection.PatientsRecoveryProtocolsSection -> PatientsRecoveryProtocolsSection(data = section.data)
        is CmsSection.PatientsFeaturesAccordionSection -> PatientsFeaturesAccordionSection(data = section.data)
        is CmsSection.PatientsPricingOptionsSection -> PatientsPricingOptionsSection(data = section.data)
        is CmsSection.PatientsFaqSection -> PatientsFaqSection(data = section.data)
        is CmsSection.PatientsCtaSection -> PatientsCtaSection(data = section.data)

        // Clinicians
        is CmsSection.CliniciansHeroSection -> CliniciansHeroSection(data = section.data)
        is CmsSection.CliniciansImpactBarSection -> CliniciansImpactBarSection(data = section.data)
        is CmsSection.CliniciansWorkflowSection -> CliniciansWorkflowSection(data = section.data)
        is CmsSection.CliniciansImplementationSection -> CliniciansImplementationSection(data = section.data)
        is CmsSection.CliniciansTargetedMessagingSection -> CliniciansTargetedMessagingSection(data = section.data)
        is CmsSection.CliniciansEvidenceSection -> CliniciansEvidenceSection(data = section.data)
        is CmsSection.CliniciansProcurementSection -> CliniciansProcurementSection(data = section.data)
        is CmsSection.CliniciansFaqSection -> CliniciansFaqSection(data = section.data)
        is CmsSection.CliniciansCtaSection -> CliniciansCtaSection(data = section.data)

        // About
        is CmsSection.AboutHeroSection -> AboutHeroSection(data = section.data)
        is CmsSection.AboutOriginSection -> AboutOriginSection(data = section.data)
        is CmsSection.AboutFounderLetterSection -> AboutFounderLetterSection(data = section.data)
        is CmsSection.AboutValuesSection -> AboutValuesSection(data = section.data)
        is CmsSection.AboutLeadershipSection -> AboutLeadershipSection(data = section.data)
        is CmsSection.AboutTrustSection -> AboutTrustSection(data = section.data)
        is CmsSection.AboutCareersSection -> AboutCareersSection(data = section.data)
        is CmsSection.AboutCtaSection -> AboutCtaSection(data = section.data)

        // Contact
        is CmsSection.ContactRoutingSection -> ContactRoutingSection(data = section.data)
        is CmsSection.ContactB2bDemoSection -> ContactB2bDemoSection(data = section.data)
        is CmsSection.ContactPatientFormSection -> ContactPatientFormSection(data = section.data)
        is CmsSection.ContactSupportSection -> ContactSupportSection(data = section.data)

        // News
        is CmsSection.NewsFeaturedSection -> NewsFeaturedSection(data = section.data)
        is CmsSection.NewsGridSection -> NewsGridSection(data = section.data)
        is CmsSection.NewsPressKitSection -> NewsPressKitSection(data = section.data)

        // Resources
        is CmsSection.ResourcesHeroSection -> ResourcesHeroSection(data = section.data)
        is CmsSection.ResourcesFeaturedSection -> ResourcesBrochureDownloadSection(data = section.data)
        is CmsSection.ResourcesGridSection -> ResourcesGridSection(data = section.data)
        is CmsSection.ResourcesCtaSection -> ResourcesCtaSection(data = section.data)

        // Testimonials
        is CmsSection.TestimonialsBenefitsSection -> TestimonialsBenefitsSection(data = section.data)
        is CmsSection.PatientsRecoveryStoriesSection -> PatientsRecoveryStoriesSection(data = section.data)
        is CmsSection.TestimonialsCtaSection -> TestimonialsCtaSection(data = section.data)

        // Financing
        is CmsSection.FinancingPartnersSection -> FinancingPartnersSection(data = section.data)
        is CmsSection.FinancingCtaSection -> FinancingCtaSection(data = section.data)

        // Distributors
        is CmsSection.DistributorsHeroSection -> DistributorsHeroSection(data = section.data)
        is CmsSection.DistributorsListSection -> DistributorsListSection(data = section.data)
        is CmsSection.DistributorsCtaSection -> DistributorsCtaSection(data = section.data)

        // Clinics
        is CmsSection.ClinicsHeroSection -> ClinicsHeroSection(data = section.data)
        is CmsSection.ClinicsListSection -> ClinicsListSection(data = section.data)
        is CmsSection.ClinicsCtaSection -> ClinicsCtaSection(data = section.data)

        // Common
        is CmsSection.ClinicalApplicationsSection -> ClinicalApplicationsSection(data = section.data)

        is CmsSection.CorporatePolicySection -> CorporatePolicySection(section.data)
        is CmsSection.CookieMatrixSection -> CookieMatrixSection(section.data)
        is CmsSection.TermsOfServiceSection -> TermsOfServiceSection(section.data)
        is CmsSection.NotFoundSection -> NotFoundSection(section.data)
    }
}